"use client";

import { PageHeader } from "@/components/common/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Loading } from "@/components/ui/loading";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/features/auth/context/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Task } from "../types";

interface CompanyUser {
  id: string;
  given_name: string;
  family_name: string;
}

export default function TaskForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { profile } = useAuth();
  const [date, setDate] = useState<Date>();
  const [task, setTask] = useState<Task | null>(null);
  const [loading, setLoading] = useState(!!id);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [companyUsers, setCompanyUsers] = useState<CompanyUser[]>([]);
  const [open, setOpen] = useState(false);
  const [selectedAssignees, setSelectedAssignees] = useState<string[]>([]);

  useEffect(() => {
    async function fetchCompanyId() {
      try {
        if (profile.anonymous) throw new Error("No user found");

        const { data: profileData, error: profileError } = await supabase
          .from("profiles")
          .select("company_id")
          .eq("id", profile.id)
          .single();

        if (profileError) throw profileError;
        if (!profileData.company_id) throw new Error("No company_id found");

        setCompanyId(profileData.company_id);
      } catch (error) {
        console.error("Error fetching company ID:", error);
        toast({
          title: "Fehler",
          description: "Fehler beim Laden der Unternehmensdaten.",
          variant: "destructive"
        });
      }
    }

    if (!profile.anonymous) {
      fetchCompanyId();
    }
  }, [profile, toast]);

  useEffect(() => {
    async function fetchCompanyUsers() {
      try {
        if (profile.anonymous) throw new Error("No user found");

        const { data: userProfile, error: profileError } = await supabase
          .from("profiles")
          .select("company_id")
          .eq("id", profile.id)
          .single();

        if (profileError) throw profileError;
        if (!userProfile?.company_id) throw new Error("No company_id found");

        const { data: users, error: usersError } = await supabase
          .from("profiles")
          .select("id, given_name, family_name")
          .eq("company_id", userProfile.company_id);

        if (usersError) throw usersError;
        setCompanyUsers(users || []);
      } catch (error) {
        console.error("Error fetching company users:", error);
      }
    }

    if (!profile.anonymous) {
      fetchCompanyUsers();
    }
  }, [profile]);

  useEffect(() => {
    async function fetchTask() {
      if (!id) return;

      try {
        // First fetch the task
        const { data: taskData, error: taskError } = await supabase
          .from("tasks")
          .select("*")
          .eq("id", id)
          .single();

        if (taskError) throw taskError;

        // Then fetch the assignments separately
        const { data: assignmentsData, error: assignmentsError } =
          await supabase
            .from("task_assignments")
            .select(
              `
                        user_id,
                        profiles (
                            given_name,
                            family_name
                        )
                    `
            )
            .eq("task_id", id);

        if (assignmentsError) throw assignmentsError;

        setTask(taskData);
        if (taskData.due_date) {
          setDate(new Date(taskData.due_date));
        }
        if (assignmentsData) {
          setSelectedAssignees(assignmentsData.map((a) => a.user_id));
        }
      } catch (err) {
        console.error("Error fetching task:", err);
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setLoading(false);
      }
    }

    fetchTask();
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!task || !companyId) return;
    setSaving(true);

    try {
      const taskData = {
        ...task,
        company_id: companyId,
        due_date: date?.toISOString(),
        updated_at: new Date().toISOString(),
        completed_at:
          task.status === "completed" ? new Date().toISOString() : null
      };

      let taskId: string;

      if (id) {
        const { data, error } = await supabase
          .from("tasks")
          .update(taskData)
          .eq("id", task.id)
          .select()
          .single();

        if (error) throw error;
        taskId = data.id;
      } else {
        const { data, error } = await supabase
          .from("tasks")
          .insert([taskData])
          .select()
          .single();

        if (error) throw error;
        taskId = data.id;
      }

      // Handle assignments
      const { error: deleteError } = await supabase
        .from("task_assignments")
        .delete()
        .eq("task_id", taskId);

      if (deleteError) throw deleteError;

      if (selectedAssignees.length > 0) {
        const { error: insertError } = await supabase
          .from("task_assignments")
          .insert(
            selectedAssignees.map((userId) => ({
              task_id: taskId,
              user_id: userId
            }))
          );

        if (insertError) throw insertError;
      }

      toast({
        title: "Erfolg",
        description: id
          ? "Die Aufgabe wurde erfolgreich aktualisiert."
          : "Die Aufgabe wurde erfolgreich erstellt."
      });
      navigate("/tasks");
    } catch (error) {
      console.error("Error saving task:", error);
      toast({
        title: "Fehler",
        description: `${id ? "Aktualisierung" : "Erstellung"} der Aufgabe fehlgeschlagen. Bitte versuche es erneut.`,
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loading text="Aufgabe laden..." />;

  if (error)
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-lg text-red-500">Fehler: {error}</div>
      </div>
    );

  return (
    <div className="flex h-screen bg-white">
      <div className="flex-1 flex flex-col overflow-hidden">
        <PageHeader
          breadcrumbs={[
            { title: "Startseite", href: "/" },
            { title: "Aufgaben", href: "/tasks" },
            { title: id ? "Aufgabe bearbeiten" : "Neue Aufgabe" }
          ]}
        />

        <div className="flex-1 overflow-auto">
          <div className="p-6">
            <div className="max-w-2xl">
              <div className="space-y-6">
                <div>
                  <h1 className="text-2xl font-semibold mb-2">
                    {id ? "Aufgabe bearbeiten" : "Neue Aufgabe"}
                  </h1>
                  <p className="text-gray-500">
                    {id
                      ? "Aktualisiere die Details der Aufgabe."
                      : "Erstelle eine neue Aufgabe."}
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium">Name</label>
                      <Input
                        value={task?.name || ""}
                        onChange={(e) =>
                          setTask({ ...task!, name: e.target.value })
                        }
                        placeholder="Aufgabenname"
                        required
                      />
                    </div>

                    <div>
                      <label className="text-sm font-medium">
                        Beschreibung
                      </label>
                      <Textarea
                        value={task?.description || ""}
                        onChange={(e) =>
                          setTask({ ...task!, description: e.target.value })
                        }
                        placeholder="Beschreibe die Aufgabe"
                        className="min-h-[100px]"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium">Status</label>
                        <Select
                          value={task?.status || "pending"}
                          onValueChange={(value) =>
                            setTask({ ...task!, status: value })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Status auswählen" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Ausstehend</SelectItem>
                            <SelectItem value="in_progress">
                              In Bearbeitung
                            </SelectItem>
                            <SelectItem value="completed">
                              Abgeschlossen
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <label className="text-sm font-medium">Priorität</label>
                        <Select
                          value={task?.priority || "medium"}
                          onValueChange={(value) =>
                            setTask({ ...task!, priority: value })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Priorität auswählen" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="low">Niedrig</SelectItem>
                            <SelectItem value="medium">Mittel</SelectItem>
                            <SelectItem value="high">Hoch</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-medium">
                        Fälligkeitsdatum
                      </label>
                      <Input
                        type="date"
                        value={date ? date.toISOString().split("T")[0] : ""}
                        onChange={(e) =>
                          setDate(
                            e.target.value
                              ? new Date(e.target.value)
                              : undefined
                          )
                        }
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label className="text-sm font-medium">Zugewiesene</label>
                      <Popover open={open} onOpenChange={setOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={open}
                            className="w-full justify-between"
                          >
                            <div className="flex flex-wrap gap-1">
                              {selectedAssignees.map((userId) => {
                                const user = companyUsers.find(
                                  (u) => u.id === userId
                                );
                                return user ? (
                                  <Badge key={userId} variant="secondary">
                                    {`${user.given_name} ${user.family_name}`}
                                  </Badge>
                                ) : null;
                              })}
                            </div>
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-full p-0">
                          <Command>
                            <CommandInput placeholder="Benutzer suchen..." />
                            <CommandEmpty>
                              Keine Benutzer gefunden.
                            </CommandEmpty>
                            <CommandGroup>
                              {companyUsers.map((user) => (
                                <CommandItem
                                  key={user.id}
                                  onSelect={() => {
                                    setSelectedAssignees((prev) =>
                                      prev.includes(user.id)
                                        ? prev.filter((id) => id !== user.id)
                                        : [...prev, user.id]
                                    );
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      selectedAssignees.includes(user.id)
                                        ? "opacity-100"
                                        : "opacity-0"
                                    )}
                                  />
                                  {`${user.given_name} ${user.family_name}`}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>

                    <div>
                      <label className="text-sm font-medium">Tags</label>
                      <Input
                        value={task?.tags?.join(", ") || ""}
                        onChange={(e) =>
                          setTask({
                            ...task!,
                            tags: e.target.value.split(",").map((s) => s.trim())
                          })
                        }
                        placeholder="Tags durch Kommas getrennt"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-4">
                    <Button
                      variant="outline"
                      onClick={() => navigate("/tasks")}
                    >
                      Abbrechen
                    </Button>
                    <Button type="submit" disabled={saving}>
                      {saving
                        ? "Speichern..."
                        : id
                          ? "Aktualisieren"
                          : "Erstellen"}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
